import type { LogoDialogImage } from '@/components/company-logo-dialog';
import { CompanyLogoField } from '@/components/company-logo-field';
import { NoyauPicker } from '@/components/noyau-picker';
import { companiesApi } from '@/lib/api';
import { blobToDataUrl } from '@/lib/data-url';
import { logoFileType } from '@/lib/logo-framing';
import { fileNameOfUrl, urlDetail } from '@/lib/use-stored-company-logo';
import {
  type CreateCompanyInput,
  type LogoFraming,
  type Noyau,
  createCompanyInputSchema,
} from '@mss_creator/shared';
import { Button, TextField } from '@pms-digital/design-system';
import { useState } from 'react';

/** The logo chosen on the form, kept until « Créer » since the company does not exist yet. */
export interface CompanyLogoDraft {
  file: File;
  framing: LogoFraming;
}

export interface CompanyCreateFormProps {
  onSubmit: (input: CreateCompanyInput, logo: CompanyLogoDraft | null) => void;
}

/** The draft with the object URL its circle draws from. */
interface LogoChoice extends CompanyLogoDraft {
  previewUrl: string;
}

/**
 * Company creation form — name + noyau are both required (noyau is picked at
 * creation, not deferred like the rest of the singularity setup, per
 * docs/design/.../2026-08-06 grill-me decision 3); the logo is optional (issue
 * #20): the same circle and editor as the settings, but with no company to
 * save on yet it is only kept here, and handed to `onSubmit` for the page to
 * send right after the company is created. Uncontrolled-style state + Zod
 * safeParse on submit, matching the project's "simple form" convention.
 */
export function CompanyCreateForm({ onSubmit }: CompanyCreateFormProps) {
  const [name, setName] = useState('');
  const [noyau, setNoyau] = useState<Noyau | null>(null);
  const [logo, setLogo] = useState<LogoChoice | null>(null);

  const canSubmit = name.trim().length > 0 && noyau !== null;

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit || !noyau) return;

    const parsed = createCompanyInputSchema.safeParse({ name: name.trim(), noyau });
    if (!parsed.success) return;
    onSubmit(parsed.data, logo ? { file: logo.file, framing: logo.framing } : null);
  }

  /** The circle draws from a data URL of the chosen file (see `lib/data-url.ts`). */
  async function keepLogo(file: File, framing: LogoFraming) {
    const previewUrl = await blobToDataUrl(new Blob([file], { type: logoFileType(file) }));
    setLogo({ file, framing, previewUrl });
  }

  async function importFromUrl(url: string): Promise<LogoDialogImage> {
    const blob = await companiesApi.previewLogoFromUrl(url);
    const file = new File([blob], fileNameOfUrl(url), { type: blob.type });
    const src = await blobToDataUrl(new Blob([file], { type: logoFileType(file) }));
    return { src, file, name: file.name, detail: urlDetail(url) };
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div>
        <TextField
          label="Nom"
          id="company-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-required="true"
        />
      </div>

      <div>
        <span className="font-semibold text-small">Noyau</span>
        <div className="mt-1">
          <NoyauPicker value={noyau} onChange={setNoyau} />
        </div>
      </div>

      <div>
        <span className="font-semibold text-small">Logo (optionnel)</span>
        <div className="mt-1 flex items-center gap-4">
          <CompanyLogoField
            logo={logo ? { src: logo.previewUrl, ...logo.framing } : null}
            companyName={name.trim() || 'Nouvelle entreprise'}
            onImportUrl={importFromUrl}
            onSave={async ({ file, framing }) => {
              // No file: only the framing of the logo already chosen changed.
              const kept = file ?? logo?.file;
              if (kept) await keepLogo(kept, framing);
            }}
            onRemove={async () => {
              setLogo(null);
            }}
          />
          <p className="max-w-[300px] text-gray-600 text-small">
            {logo
              ? "Logo choisi et cadré. Il sera enregistré juste après la création de l'entreprise."
              : 'Optionnel. Cliquez pour importer une image ou indiquer son adresse.'}
          </p>
        </div>
      </div>

      <Button variant="secondary" type="submit" disabled={!canSubmit}>
        Créer
      </Button>
    </form>
  );
}
